import { useEffect, useState, FC, useRef, useMemo } from "react";
import { Bubble } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ChartOptions,
  registerables,
  BubbleController,
  LinearScale,
  PointElement,
  TimeScale,
  BubbleDataPoint,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";

import { useAppSelector } from "redux/hooks";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useGetCurrentUserQuery } from "api/api-users-v2";

// Register required chart components
ChartJS.register(
  BubbleController,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
  Legend,
  ...registerables
);

interface CustomBubbleDataPoint extends BubbleDataPoint {
  rawData?: any;
}

interface ChartDataset {
  data: CustomBubbleDataPoint[];
  backgroundColor: string;
  borderWidth: number;
  label: string;
}

interface CustomChartData {
  datasets: ChartDataset[];
}

interface ActiveInvoicesVisualizationProps {
  importerData?: any;
}

export const ActiveInvoicesVisualization: FC<
  ActiveInvoicesVisualizationProps
> = ({ importerData }) => {
  const [chartData, setChartData] = useState<CustomChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<ChartJS<"bubble">>(null);

  // Get current user data
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();
  const USER_PERMISSION = useAppSelector(
    (state) => state.appState.user_permission
  );

  // Check if user has investor role or permissions
  const isInvestor = currentUser?.user_role === "Investor";
  const hasInvestorPermission = USER_PERMISSION?.includes("show_dashboard");
  const hasPermission = isInvestor || hasInvestorPermission;

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (!hasPermission) {
      setError("You do not have permission to view this visualization.");
      return;
    }

    if (!importerData || importerData.length === 0) {
      setError("No invoice data available for visualization.");
      return;
    }

    try {
      const processData = () => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const earlyPayments: CustomBubbleDataPoint[] = [];
        const onTimePayments: CustomBubbleDataPoint[] = [];
        const overduePayments: CustomBubbleDataPoint[] = [];

        const maxAmount = Math.max(
          ...importerData.map((inv: any) => inv.principle_amount || 0)
        );

        importerData.forEach((invoice: any) => {
          const actualDate = new Date(
            invoice.actual_payment_date || invoice.expected_repayment_date
          );
          const dueDate = new Date(invoice.invoice_due_date);

          const repaymentDate = actualDate;
          const daysDifference = Math.floor(
            (actualDate.getTime() - dueDate.getTime()) / (1000 * 3600 * 24)
          );

          const bubbleSize = Math.max(
            8,
            Math.min(30, (invoice.principle_amount / maxAmount) * 35)
          );

          const dataPoint: CustomBubbleDataPoint = {
            x: repaymentDate.getTime(),
            y: daysDifference,
            r: bubbleSize,
            rawData: invoice,
          };

          if (daysDifference < 0) {
            earlyPayments.push(dataPoint);
          } else if (daysDifference === 0) {
            onTimePayments.push(dataPoint);
          } else {
            overduePayments.push(dataPoint);
          }
        });

        const chartConfig: CustomChartData = {
          datasets: [
            {
              data: earlyPayments,
              backgroundColor: "rgba(126, 214, 223, 0.8)",
              borderWidth: 0,
              label: "Early Payments",
            },
            {
              data: onTimePayments,
              backgroundColor: "rgba(116, 185, 255, 0.8)",
              borderWidth: 0,
              label: "On Time Payments",
            },
            {
              data: overduePayments,
              backgroundColor: "rgba(255, 118, 117, 0.8)",
              borderWidth: 0,
              label: "Overdue Payments",
            },
          ],
        };

        setChartData(chartConfig);
        setError(null);
      };

      processData();
    } catch (err) {
      console.error("Error processing chart data:", err);
      setError("Error processing chart data. Please try again later.");
    }
  }, [importerData, hasPermission, currentUser]);

  const getNiceStep = (range: number) => {
    if (range > 1000) return 200;
    if (range > 500) return 100;
    if (range > 200) return 50;
    if (range > 100) return 20;
    if (range > 50) return 10;
    return 5;
  };

  const yMin = -Math.ceil((chartData?.datasets[0].data.length || 10) * 1.1);
  const yMax = Math.ceil((chartData?.datasets[0].data.length || 10) * 1.1);
  const yRange = yMax - yMin;
  const yStep = getNiceStep(yRange);

  const chartOptions: ChartOptions<"bubble"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      elements: {
        point: {
          borderWidth: 0,
        },
      },
      scales: {
        x: {
          type: "time",
          time: {
            unit: "month",
            displayFormats: { month: "yyyy-MM" },
            tooltipFormat: "MMM yyyy",
          },
          title: {
            display: true,
            text: "Repayment Date (Monthly)",
            font: { size: 14, weight: "bold" },
          },
          grid: { display: true, color: "rgba(0,0,0,0.1)" },
        },
        y: {
          title: {
            display: true,
            text: "Days Difference (Actual - Due)",
            font: { size: 14, weight: "bold" },
          },
          min: -70,
          max: 70,
          ticks: {
            stepSize: 10,
            callback: function (tickValue) {
              const value = Number(tickValue);
              if (value === 0) return "0";
              return value > 0 ? `+${value}` : `${value}`;
            },
          },
          grid: {
            display: true,
            color: (context) =>
              context.tick.value === 0 ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)",
            lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              const point = context.raw as CustomBubbleDataPoint;
              const invoice = point.rawData;
              const daysDiff = point.y;
              let status =
                daysDiff < 0 ? "Early" : daysDiff === 0 ? "On Time" : "Overdue";
              const amount = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(invoice.principle_amount);
              return [
                `Invoice ID: ${invoice.invoice_id}`,
                `Amount: ${amount}`,
                `Repayment Date: ${new Date(point.x).toLocaleDateString()}`,
                `Days ${status}: ${Math.abs(daysDiff)}`,
              ];
            },
          },
        },
        legend: {
          position: "top",
          labels: { usePointStyle: true, padding: 20, font: { size: 12 } },
        },
      },
    }),
    []
  );

  const header = importerData?.buyer_name
    ? `Importer Repayment Behavior - ${importerData?.buyer_name}`
    : "Invoice Payment Behavior Over Time";

  if (isUserLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        You do not have permission to view this visualization.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!chartData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No data available for visualization.
      </Alert>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardContent>
        <Typography variant="h4" gutterBottom>
          {header}
        </Typography>

        <Box height={600} position="relative" width={"100%"}>
          {chartData && (
            <Bubble
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              style={{
                width: "100%",
                height: "100%",
                imageRendering: "pixelated",
              }}
              key={JSON.stringify(chartData)}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActiveInvoicesVisualization;
